import { Preferences } from "@capacitor/preferences";
import { isNativePreviewRuntime, isNativeRuntime } from "./runtime";

const LEGACY_TOKEN_KEY = "flextech.member_portal.token";
const ACCESS_TOKEN_KEY = "flextech.member_portal.access_token";
const REFRESH_TOKEN_KEY = "flextech.member_portal.refresh_token";

let nativePreferencesAvailable: boolean | null = null;

function browserStorageKey(key: string) {
  if (!isNativePreviewRuntime()) return key;

  const previewId = document.getElementById("member-portal-root")?.dataset.memberAppPreviewId || "preview";
  return `${key}.admin-preview.${previewId}`;
}

function browserStorage() {
  return isNativePreviewRuntime() ? window.sessionStorage : window.localStorage;
}

function preferences() {
  if (!isNativeRuntime()) return null;
  return Preferences;
}

async function readPreference(key: string) {
  const prefs = preferences();
  if (!prefs) return null;

  try {
    nativePreferencesAvailable = true;
    return (await prefs.get({ key })).value;
  } catch (_error) {
    nativePreferencesAvailable = false;
    return null;
  }
}

async function writePreference(key: string, value: string) {
  const prefs = preferences();
  if (!prefs) return false;

  try {
    await prefs.set({ key, value });
    nativePreferencesAvailable = true;
    return true;
  } catch (_error) {
    nativePreferencesAvailable = false;
    return false;
  }
}

async function removePreference(key: string) {
  const prefs = preferences();
  if (!prefs) return false;

  try {
    await prefs.remove({ key });
    nativePreferencesAvailable = true;
    return true;
  } catch (_error) {
    nativePreferencesAvailable = false;
    return false;
  }
}

export const authStorage = {
  async getAccessToken() {
    const token = await readPreference(ACCESS_TOKEN_KEY);
    if (token) return token;
    return browserStorage().getItem(browserStorageKey(ACCESS_TOKEN_KEY));
  },
  async getRefreshToken() {
    const token = await readPreference(REFRESH_TOKEN_KEY);
    if (token) return token;
    return browserStorage().getItem(browserStorageKey(REFRESH_TOKEN_KEY));
  },
  async setTokens(tokens: { access_token?: string | null; refresh_token?: string | null }) {
    if (tokens.access_token) await writePreference(ACCESS_TOKEN_KEY, tokens.access_token);
    if (tokens.refresh_token) await writePreference(REFRESH_TOKEN_KEY, tokens.refresh_token);
    await removePreference(LEGACY_TOKEN_KEY);

    const storage = browserStorage();
    if (tokens.access_token) storage.setItem(browserStorageKey(ACCESS_TOKEN_KEY), tokens.access_token);
    if (tokens.refresh_token) storage.setItem(browserStorageKey(REFRESH_TOKEN_KEY), tokens.refresh_token);
    storage.removeItem(browserStorageKey(LEGACY_TOKEN_KEY));
  },
  async clearTokens() {
    await removePreference(ACCESS_TOKEN_KEY);
    await removePreference(REFRESH_TOKEN_KEY);
    await removePreference(LEGACY_TOKEN_KEY);

    const storage = browserStorage();
    storage.removeItem(browserStorageKey(ACCESS_TOKEN_KEY));
    storage.removeItem(browserStorageKey(REFRESH_TOKEN_KEY));
    storage.removeItem(browserStorageKey(LEGACY_TOKEN_KEY));
  }
};
